import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import m from 'mithril';
import { resolvePartUrl, diag } from '../utils/tagMatch';
import { loadTags } from '../utils/tagsSource';
import { isLandingSeen, markLandingSeen } from '../utils/landingState';
import navigateTo from '../utils/navigate';

/*
 * 手机端首页「一屏着陆页」（绿色主题，视觉沿用公众号长图文风格）。
 *
 * 由 index.js 单独 m.mount 到 body 下的根节点（与底部班级导航同理），不挂在
 * IndexPage 上：后台「外观 → 首页」可以把 Tags 页设成默认首页，那时 IndexPage
 * 不渲染，注入会失效；这里改成自己判断 URL 路径，首页无论渲染哪个页面都生效。
 *
 * 外层 .AcmeLanding 是 position:fixed 的全屏覆盖层，会把首页的讨论列表
 * / 标签列表 / 侧边栏 / 底部班级导航整个遮住 —— 手机打开域名看到的就是这一屏。
 * 点「进入班级」或任一板块卡片后本层卸掉，露出下方正常的首页内容 / 标签页。
 *
 * 生效条件（三者必须同时成立）：
 *   1. 当前 URL 是站点根路径（m.route.get() === '/'）
 *   2. 设备是手机：UA 命中移动端 或 视口宽度 <= 768px（PC 端直接不显示）
 *   3. 本次进入首页后尚未点击（离开首页会自动复位）
 *
 * 一屏不滚动：内部 .AcmeLanding-inner 用 flex 纵向 + space-between 三段布局，
 * 中间的 2x2 板块网格 flex:1 吃掉剩余空间，字号/间距全部用 clamp(min, vh, max)
 * 跟着屏幕高度缩放，所以从 iPhone SE 到 iPhone 15 Pro Max 都不会出现滑动条。
 *
 * 文案与四个板块链接取自 design/banner-prototype.html（用户已定稿的版本）。
 */

/*
 * 四个板块。
 *
 * key   —— 板块 slug 关键字，是匹配次标签的**唯一主要依据**（大小写不敏感，
 *          分隔符无所谓）。必须与后台次标签 slug 的后半段一致：
 *            AI02-OnClass / AI02-task / AI02-Sharing / AI02-Suggestion
 * title —— 卡片上显示的中文字（取自 design/banner-prototype.html 定稿版）
 * en    —— 卡片上的英文小标
 *
 * 注意：卡片显示的是短名（课程/作业/交流/班会），后台标签实际叫
 * 在线课堂/作业专区/经验交流/班干会议 —— 两者可以不同，匹配不走中文名。
 * 若要让卡片文字与后台标签名一致，把 title 改成后台名字即可（key 不变）。
 */
const PARTS = [
  { num: '01', key: 'OnClass', title: '课程', alt: '在线课堂', en: 'Course', desc: '跟随班级进度学习，随时回看' },
  { num: '02', key: 'task', title: '作业', alt: '作业专区', en: 'Homework', desc: '按时提交，查看批改与反馈' },
  { num: '03', key: 'Sharing', title: '交流', alt: '经验交流', en: 'Discuss', desc: '有问题随时提问，有好想法就分享' },
  { num: '04', key: 'Suggestion', title: '班会', alt: '班干会议', en: 'Resource', desc: '班里的大事小事可以在这里讨论' },
];

/*
 * 是否显示着陆页。仅在手机（含平板小屏）为真，PC 端恒为 false。
 *
 * 调试开关：
 *   ?landing=preview  强制显示（浏览器把窗口拖窄也能预览）
 *   ?landing=off      强制不显示
 */
// ?landing=preview 强制显示（调试用，会忽略「已看过」状态）
function forcedPreview() {
  try {
    return new URLSearchParams(window.location.search).get('landing') === 'preview';
  } catch (e) {
    return false;
  }
}

function shouldShowLanding() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('landing') === 'preview') return true;
    if (params.get('landing') === 'off') return false;
  } catch (e) {
    // URL 解析失败不影响主流程，继续走设备判断
  }

  try {
    const ua = (window.navigator && window.navigator.userAgent) || '';
    const mobileUA = /Android|iPhone|iPod|iPad|Windows Phone|HarmonyOS|IEMobile|Opera Mini|Mobile/i.test(ua);
    const narrow = window.matchMedia
      ? window.matchMedia('(max-width: 768px)').matches
      : window.innerWidth <= 768;

    // PC 浏览器正常宽度时为 false → 完全不渲染这块 DOM
    return mobileUA || narrow;
  } catch (e) {
    return false;
  }
}

/*
 * 当前是否处于站点首页。
 *
 * 用 URL 路径判断，而不是判断渲染的是不是 IndexPage：
 * 后台「外观 → 首页」可以指定 Tags 页等其它页面作为首页，
 * 那种情况下 URL 仍是 '/'，但 IndexPage 不会渲染。
 */
function isHomePath() {
  try {
    // m.route.get() 返回不带 hash 的路径（论坛端 route prefix 为空）
    const path = (m.route.get() || '').split('?')[0].replace(/\/+$/, '');
    return path === '' || path === '/';
  } catch (e) {
    return false;
  }
}

/*
 * 「本次会话已看过着陆页」的状态放在 utils/landingState.js（sessionStorage）。
 *
 * 之前用模块级变量，只要发生整页跳转或刷新（普通 <a href> 未被 mithril 拦截时
 * 就会发生）JS 全部重载、变量清零，着陆页会再次弹出。改用 sessionStorage 后，
 * SPA 跳转、整页跳转、F5 刷新都不再重复显示，关闭标签页才重现。
 */

export default class ClassHubHero extends Component {
  oninit(vnode) {
    super.oninit(vnode);

    this.dismissed = false;
    this.visible = shouldShowLanding();

    /*
     * 必须拿到完整标签列表，四张卡片才能解析到「本班下的次标签」。
     *
     * 【踩坑记录】之前用 app.store.find('tags') + app.store.all('tags')，
     * 但 store 里的标签经常为空或只有主标签（首屏 payload 不全），
     * 匹配必然失败 → 四张卡片的 href 全部退化成 '/'，点下去毫无反应。
     *
     * 改为 utils/tagsSource.js 自己请求 /api/tags 拿全量数据，
     * 拿到后重绘，卡片地址自动更新。
     */
    if (this.visible) {
      loadTags().then(
        () => {
          try {
            window.__acmeTags = diag();
          } catch (e) {
            // ignore
          }
          m.redraw();
        },
        () => {}
      );
    }
  }

  oncreate(vnode) {
    super.oncreate(vnode);

    /*
     * 【1.8.36.34 修复】这里不再 add AcmeLanding-open！
     *
     * 两个原因：
     * ① Mithril 对 view() 返回 null 的组件【同样会触发 oncreate】，
     *    而本组件被 m.mount 到 body 常驻节点、PC 端也挂载 ——
     *    之前无条件 add，导致 PC 端 body 一直挂着 AcmeLanding-open
     *    （forum.less: body.AcmeLanding-open{overflow:hidden}）→ 滚轮失效。
     *    且 view() 里 !this.visible 的提前 return 摘不掉这个类 → 永久锁死。
     *    （F12 手机模拟"滚轮有用"是因为着陆页真渲染了、dismiss() 摘了类）
     * ② 手机端第二次进首页（isLandingSeen()=true，着陆页不再渲染）也会被同样锁死。
     *
     * 锁/解锁统一收口到 view()：按「这一帧到底渲不渲染着陆页」决定，
     * 渲染才锁，不渲染（无论何种原因）必解锁。
     */
  }

  onremove(vnode) {
    super.onremove(vnode);

    document.body.classList.remove('AcmeLanding-open');
  }

  // 卸掉着陆层（不跳转）：「进入班级」按钮用
  enterCommunity() {
    this.dismiss();
    window.scrollTo(0, 0);
    m.redraw();
  }

  // 点板块卡片：先卸掉着陆层，再跳到对应标签页
  // （navigateTo 内部先走 SPA，300ms 后若地址没变则用原生跳转兜底）
  goToTag(e, url) {
    e.preventDefault();
    this.dismiss();
    navigateTo(url);
  }

  // 标记本次会话已看过，并解锁背景滚动
  dismiss() {
    this.dismissed = true;
    markLandingSeen();
    document.body.classList.remove('AcmeLanding-open');
  }

  view() {
    /*
     * 锁/解锁统一在这里做：body.AcmeLanding-open 是否存在，
     * 完全由「这一帧着陆页到底渲不渲染」决定 —— 渲染才锁，
     * 不渲染（PC 端 / 非首页 / 已看过 / 已点掉）一律解锁。
     * 这样任何 early return 都不可能把锁漏在 body 上。
     */
    const show =
      this.visible &&
      isHomePath() &&
      (forcedPreview() || (!this.dismissed && !isLandingSeen()));
    document.body.classList.toggle('AcmeLanding-open', show);

    if (!show) return null;

    return (
      <div className="AcmeLanding">
        <div className="AcmeLanding-inner">
          <header className="AcmeLanding-head">
            <div className="AcmeLanding-meta">
              线上班级 · <b>LEARNING HUB</b>
            </div>
            <h1 className="AcmeLanding-title">
              在这里，和同班同学
              <br />
              一起把课学透
            </h1>
            <div className="AcmeLanding-sub">Welcome to your class hub</div>
          </header>

          <div className="AcmeLanding-grid">
            {PARTS.map((p) => {
              const url = resolvePartUrl(p);

              return (
                <a className="AcmeLanding-card" href={url} onclick={(e) => this.goToTag(e, url)}>
                  <div className="AcmeLanding-num">{p.num}</div>
                  <div className="AcmeLanding-cardTitle">{p.title}</div>
                  <div className="AcmeLanding-cardEn">{p.en}</div>
                  <div className="AcmeLanding-cardDesc">{p.desc}</div>
                </a>
              );
            })}
          </div>

          <footer className="AcmeLanding-foot">
            <p className="AcmeLanding-slogan">把复杂藏起来，只留下安静学习的地方。</p>
            <button type="button" className="AcmeLanding-enter" onclick={() => this.enterCommunity()}>
              进入班级 →
            </button>
          </footer>
        </div>
      </div>
    );
  }
}
